/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Hanly API v2
 * OpenAPI spec version: 0.0.1
 */

import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { ErrorType } from '../../custom-client';

import { customInstance } from '../../custom-client';
import type {
  DetailError,
  MfaTokenObtainRequest,
  OfficeSignInRequest,
  OfficeSignInUnauthorized,
  StandardizedError,
  TokenObtainResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
} from '../hanlyAPIV2.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getSignoutUrl = () => {
  return '/api/v2/accounts/signout';
};

/**
 * 明示的なサインアウトを監査ログに記録する。JWT はステートレスなのでトークンは失効しない。画面はトークンを破棄する前に呼ぶ。
 * @summary サインアウト
 */
export const signout = async (options?: Parameters<typeof customInstance>[1]): Promise<null> => {
  return customInstance<null>(getSignoutUrl(), {
    ...options,
    method: 'POST',
  });
};

export const getSignoutMutationKey = () => ['signout'] as const;

export const getSignoutMutationOptions = <
  TError = ErrorType<StandardizedError | DetailError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof signout>>, TError, void, TContext>;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<Awaited<ReturnType<typeof signout>>, TError, void, TContext> => {
  const mutationKey = getSignoutMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof signout>>, void> = () => {
    return signout(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SignoutMutationResult = NonNullable<Awaited<ReturnType<typeof signout>>>;

export type SignoutMutationError = ErrorType<StandardizedError | DetailError>;

/**
 * @summary サインアウト
 */
export const useSignout = <TError = ErrorType<StandardizedError | DetailError>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof signout>>, TError, void, TContext>;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<typeof signout>>, TError, void, TContext> => {
  return useMutation(getSignoutMutationOptions(options), queryClient);
};
export const getTokenObtainUrl = () => {
  return '/api/v2/accounts/token';
};

/**
 * MFA が有効なユーザーはトークンの代わりに 401 で `code: MFA_REQUIRED` と `mfa_token` を返すので、認証アプリのコードと一緒に `/api/v2/accounts/token/mfa` へ送ること。
 * @summary ログインしてJWTトークンを取得する
 */
export const tokenObtain = async (
  officeSignInRequest: OfficeSignInRequest,
  options?: Parameters<typeof customInstance>[1],
): Promise<TokenObtainResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return customInstance<TokenObtainResponse>(getTokenObtainUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(officeSignInRequest),
  });
};

export const getTokenObtainMutationKey = () => ['tokenObtain'] as const;

export const getTokenObtainMutationOptions = <
  TError = ErrorType<StandardizedError | OfficeSignInUnauthorized | DetailError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof tokenObtain>>,
    TError,
    TokenObtainMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof tokenObtain>>,
  TError,
  TokenObtainMutationVariables,
  TContext
> => {
  const mutationKey = getTokenObtainMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof tokenObtain>>,
    TokenObtainMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return tokenObtain(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type TokenObtainMutationResult = NonNullable<Awaited<ReturnType<typeof tokenObtain>>>;
export type TokenObtainMutationBody = OfficeSignInRequest;
export type TokenObtainMutationError = ErrorType<StandardizedError | OfficeSignInUnauthorized | DetailError>;
export type TokenObtainMutationVariables = { data: OfficeSignInRequest };

/**
 * @summary ログインしてJWTトークンを取得する
 */
export const useTokenObtain = <
  TError = ErrorType<StandardizedError | OfficeSignInUnauthorized | DetailError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof tokenObtain>>,
      TError,
      TokenObtainMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof tokenObtain>>,
  TError,
  TokenObtainMutationVariables,
  TContext
> => {
  return useMutation(getTokenObtainMutationOptions(options), queryClient);
};
export const getTokenObtainMfaUrl = () => {
  return '/api/v2/accounts/token/mfa';
};

/**
 * @summary ログインで返された mfa_token と MFA コードを検証してJWTトークンを取得する
 */
export const tokenObtainMfa = async (
  mfaTokenObtainRequest: MfaTokenObtainRequest,
  options?: Parameters<typeof customInstance>[1],
): Promise<TokenObtainResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return customInstance<TokenObtainResponse>(getTokenObtainMfaUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mfaTokenObtainRequest),
  });
};

export const getTokenObtainMfaMutationKey = () => ['tokenObtainMfa'] as const;

export const getTokenObtainMfaMutationOptions = <
  TError = ErrorType<StandardizedError | DetailError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof tokenObtainMfa>>,
    TError,
    TokenObtainMfaMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof tokenObtainMfa>>,
  TError,
  TokenObtainMfaMutationVariables,
  TContext
> => {
  const mutationKey = getTokenObtainMfaMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof tokenObtainMfa>>,
    TokenObtainMfaMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return tokenObtainMfa(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type TokenObtainMfaMutationResult = NonNullable<Awaited<ReturnType<typeof tokenObtainMfa>>>;
export type TokenObtainMfaMutationBody = MfaTokenObtainRequest;
export type TokenObtainMfaMutationError = ErrorType<StandardizedError | DetailError>;
export type TokenObtainMfaMutationVariables = { data: MfaTokenObtainRequest };

/**
 * @summary ログインで返された mfa_token と MFA コードを検証してJWTトークンを取得する
 */
export const useTokenObtainMfa = <TError = ErrorType<StandardizedError | DetailError>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof tokenObtainMfa>>,
      TError,
      TokenObtainMfaMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof tokenObtainMfa>>,
  TError,
  TokenObtainMfaMutationVariables,
  TContext
> => {
  return useMutation(getTokenObtainMfaMutationOptions(options), queryClient);
};
export const getTokenRefreshUrl = () => {
  return '/api/v2/accounts/token/refresh';
};

/**
 * @summary アクセストークンを更新する
 */
export const tokenRefresh = async (
  tokenRefreshRequest: TokenRefreshRequest,
  options?: Parameters<typeof customInstance>[1],
): Promise<TokenRefreshResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return customInstance<TokenRefreshResponse>(getTokenRefreshUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tokenRefreshRequest),
  });
};

export const getTokenRefreshMutationKey = () => ['tokenRefresh'] as const;

export const getTokenRefreshMutationOptions = <
  TError = ErrorType<StandardizedError | DetailError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof tokenRefresh>>,
    TError,
    TokenRefreshMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof tokenRefresh>>,
  TError,
  TokenRefreshMutationVariables,
  TContext
> => {
  const mutationKey = getTokenRefreshMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof tokenRefresh>>,
    TokenRefreshMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return tokenRefresh(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type TokenRefreshMutationResult = NonNullable<Awaited<ReturnType<typeof tokenRefresh>>>;
export type TokenRefreshMutationBody = TokenRefreshRequest;
export type TokenRefreshMutationError = ErrorType<StandardizedError | DetailError>;
export type TokenRefreshMutationVariables = { data: TokenRefreshRequest };

/**
 * @summary アクセストークンを更新する
 */
export const useTokenRefresh = <TError = ErrorType<StandardizedError | DetailError>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof tokenRefresh>>,
      TError,
      TokenRefreshMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof tokenRefresh>>,
  TError,
  TokenRefreshMutationVariables,
  TContext
> => {
  return useMutation(getTokenRefreshMutationOptions(options), queryClient);
};
